import { supabase } from "./client";

export interface ArquivoData {
  id: string;
  url: string;
  nome: string;
  posicao: string; // ex: "Frente", "Verso", "Logo", etc
  descricao?: string;
  tamanho_kb: number;
  mimetype: string;
  criado_em: string;
}

const BUCKET_NAME = "quotes"; // Usar bucket 'quotes' que tem RLS policies configuradas
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];

export async function uploadArquivo(
  file: File,
  posicao: string,
  descricao?: string
): Promise<{ ok: boolean; data?: ArquivoData; error?: string }> {
  try {
    // Validar arquivo
    if (file.size > MAX_FILE_SIZE) {
      return { ok: false, error: "Arquivo muito grande. Máximo 10MB" };
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      return { ok: false, error: "Tipo de arquivo não permitido. Use PNG, JPG, GIF, WebP ou PDF" };
    }

    // Gerar ID único para o arquivo
    const arquivoId = crypto.randomUUID();
    const timestamp = Date.now();
    const ext = file.name.split(".").pop();
    const storagePath = `arquivos/${arquivoId}-${timestamp}.${ext}`;

    // Upload para Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(storagePath, file);

    if (uploadError) {
      console.error("Erro ao fazer upload:", uploadError);
      return { ok: false, error: `Erro no upload: ${uploadError.message}` };
    }

    // Obter URL público
    const { data: urlData } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(storagePath);

    if (!urlData?.publicUrl) {
      return { ok: false, error: "Não foi possível obter URL do arquivo" };
    }

    const arquivoData: ArquivoData = {
      id: arquivoId,
      url: urlData.publicUrl,
      nome: file.name,
      posicao,
      descricao,
      tamanho_kb: Math.round(file.size / 1024),
      mimetype: file.type,
      criado_em: new Date().toISOString(),
    };

    return { ok: true, data: arquivoData };
  } catch (error: any) {
    console.error("Erro ao fazer upload do arquivo:", error);
    return { ok: false, error: error?.message ?? "Erro desconhecido no upload" };
  }
}

export async function deleteArquivo(
  tableType: "quotes" | "orders",
  recordId: string,
  arquivoId: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    // Obter lista de arquivos atual
    const { data, error: fetchError } = await supabase
      .from(`atelie_${tableType}`)
      .select("arquivos")
      .eq("id", recordId)
      .single();

    if (fetchError) throw fetchError;

    const arquivos = (data?.arquivos || []) as ArquivoData[];
    const arquivo = arquivos.find((a) => a.id === arquivoId);

    if (!arquivo) {
      return { ok: false, error: "Arquivo não encontrado" };
    }

    // Deletar do Storage (extrair path da URL)
    const urlParts = arquivo.url.split("/");
    const storagePath = urlParts.slice(-2).join("/"); // "arquivos/..."

    const { error: deleteStorageError } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([storagePath]);

    if (deleteStorageError) {
      console.warn("Aviso ao deletar do storage:", deleteStorageError);
      // Não falhar se storage delete falhar - continuar com banco de dados
    }

    // Remover do JSONB
    const arquivosAtualizados = arquivos.filter((a) => a.id !== arquivoId);

    const { error: updateError } = await supabase
      .from(`atelie_${tableType}`)
      .update({ arquivos: arquivosAtualizados })
      .eq("id", recordId);

    if (updateError) throw updateError;

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao deletar arquivo:", error);
    return { ok: false, error: error?.message ?? "Erro ao deletar arquivo" };
  }
}

export async function updateArquivoMetadata(
  tableType: "quotes" | "orders",
  recordId: string,
  arquivoId: string,
  updates: { posicao?: string; descricao?: string }
): Promise<{ ok: boolean; error?: string }> {
  try {
    // Obter lista de arquivos atual
    const { data, error: fetchError } = await supabase
      .from(`atelie_${tableType}`)
      .select("arquivos")
      .eq("id", recordId)
      .single();

    if (fetchError) throw fetchError;

    const arquivos = (data?.arquivos || []) as ArquivoData[];
    const arquivosAtualizados = arquivos.map((a) =>
      a.id === arquivoId
        ? {
            ...a,
            ...(updates.posicao !== undefined && { posicao: updates.posicao }),
            ...(updates.descricao !== undefined && { descricao: updates.descricao }),
          }
        : a
    );

    const { error: updateError } = await supabase
      .from(`atelie_${tableType}`)
      .update({ arquivos: arquivosAtualizados })
      .eq("id", recordId);

    if (updateError) throw updateError;

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao atualizar metadados do arquivo:", error);
    return { ok: false, error: error?.message ?? "Erro ao atualizar arquivo" };
  }
}

export async function getArquivos(
  tableType: "quotes" | "orders",
  recordId: string
): Promise<ArquivoData[]> {
  try {
    const { data, error } = await supabase
      .from(`atelie_${tableType}`)
      .select("arquivos")
      .eq("id", recordId)
      .single();

    if (error) throw error;
    return (data?.arquivos || []) as ArquivoData[];
  } catch (error) {
    console.error("Erro ao obter arquivos:", error);
    return [];
  }
}

export async function addArquivoToRecord(
  tableType: "quotes" | "orders",
  recordId: string,
  arquivo: ArquivoData
): Promise<{ ok: boolean; error?: string }> {
  try {
    // Obter lista de arquivos atual
    const { data, error: fetchError } = await supabase
      .from(`atelie_${tableType}`)
      .select("arquivos")
      .eq("id", recordId)
      .single();

    if (fetchError) throw fetchError;

    const arquivos = (data?.arquivos || []) as ArquivoData[];
    arquivos.push(arquivo);

    const { error: updateError } = await supabase
      .from(`atelie_${tableType}`)
      .update({ arquivos })
      .eq("id", recordId);

    if (updateError) throw updateError;

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao adicionar arquivo:", error);
    return { ok: false, error: error?.message ?? "Erro ao adicionar arquivo" };
  }
}

export async function syncArquivosQuoteToOrder(
  quoteId: string,
  orderId: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    // Obter arquivos do orçamento
    const arquivosQuote = await getArquivos("quotes", quoteId);

    // Adicionar ao pedido
    const { data, error: fetchError } = await supabase
      .from("atelie_orders")
      .select("arquivos")
      .eq("id", orderId)
      .single();

    if (fetchError) throw fetchError;

    const arquivosPedido = (data?.arquivos || []) as ArquivoData[];
    const arquivosAtualizados = [...arquivosPedido, ...arquivosQuote];

    const { error: updateError } = await supabase
      .from("atelie_orders")
      .update({ arquivos: arquivosAtualizados })
      .eq("id", orderId);

    if (updateError) throw updateError;

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao sincronizar arquivos:", error);
    return { ok: false, error: error?.message ?? "Erro ao sincronizar arquivos" };
  }
}
